import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { usePlayerAuth } from "../../context/PlayerAuthContext";
import { toast } from "sonner";
import { Users, ChevronDown, Plus, Check, Crown, Loader2, X, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TIER_LABEL = { elite: "Elite", academy: "Technical Track", ebook: "Blueprint" };
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function Avatar({ p, size = 34 }) {
  const initial = (p.display_name || p.name || "P")[0].toUpperCase();
  return p.avatar_url ? (
    <img loading="lazy" decoding="async" src={`${BACKEND}${p.avatar_url}`} alt="" style={{ width: size, height: size }} className="rounded-full object-cover" />
  ) : (
    <div style={{ width: size, height: size }} className="rounded-full bg-white/10 text-white/70 font-bold flex items-center justify-center text-sm">{initial}</div>
  );
}

export default function ProfileSwitcher() {
  const { player, householdProfiles, switchProfile, addChild, refresh } = usePlayerAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [jersey, setJersey] = useState("");
  const [pricing, setPricing] = useState(null);
  const [upgradeFor, setUpgradeFor] = useState(null);
  const ref = useRef(null);

  const load = () => householdProfiles().then(setData).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [player?.id]);
  useEffect(() => { axios.get(`${API}/pricing`).then(({ data }) => setPricing(data)).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setAdding(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const profiles = data?.profiles || [];
  // Only show the switcher once a household actually has more than one profile OR the parent can add.
  if (profiles.length <= 1 && !data?.can_add) return null;

  const doSwitch = async (id) => {
    if (id === player?.id) { setOpen(false); return; }
    setBusy(true);
    try { const p = await switchProfile(id); toast.success(`Switched to ${p.display_name || p.name}.`); setOpen(false); }
    catch { toast.error("Couldn't switch player."); }
    finally { setBusy(false); }
  };

  const doAdd = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Enter the player's name."); return; }
    setBusy(true);
    try {
      const child = await addChild(name.trim(), jersey.trim());
      toast.success(`${child.display_name} added.`);
      setName(""); setJersey(""); setAdding(false);
      await load();
      await switchProfile(child.id);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add player.");
    } finally { setBusy(false); }
  };

  const membershipsOpen = !!pricing?.memberships_open;
  const buyForChild = async (profileId, tier) => {
    if (!membershipsOpen) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/household/checkout`,
        { profile_id: profileId, tier, origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${localStorage.getItem("playerToken")}` } });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout.");
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref} data-testid="profile-switcher">
      <button data-testid="profile-switcher-btn" onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 min-h-[44px] bg-white/5 border border-white/15 hover:border-white/40 rounded-full pl-1.5 pr-2.5 py-1.5 transition-colors">
        <Avatar p={player || {}} size={26} />
        <span className="text-xs font-bold text-white/80 max-w-[90px] truncate hidden sm:inline">{player?.display_name || player?.name || "Player"}</span>
        <ChevronDown size={14} className="text-white/50" />
      </button>

      {open && (
        <div data-testid="profile-switcher-menu" className="absolute right-0 mt-2 w-64 bg-[#161616] border border-white/12 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-3 py-2.5 border-b border-white/8 flex items-center gap-2">
            <Users size={14} className="text-[#C41E24]" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">Household Players</span>
          </div>
          <div className="max-h-80 overflow-y-auto py-1">
            {profiles.map((p) => (
              <div key={p.id}>
                <div className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/5 transition-colors">
                  <button data-testid={`profile-option-${p.id}`} onClick={() => doSwitch(p.id)} disabled={busy}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left disabled:opacity-60">
                    <Avatar p={p} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-white truncate flex items-center gap-1.5">
                        {p.display_name || p.name}
                        {p.jersey_number && <span className="text-[10px] text-white/40">#{p.jersey_number}</span>}
                      </div>
                      <div className="text-[10px] uppercase tracking-wider flex items-center gap-1" style={{ color: p.tier === "elite" ? "#D4AF37" : p.tier === "academy" ? "#60A5FA" : "rgba(255,255,255,0.4)" }}>
                        {p.tier === "elite" && <Crown size={9} />}{TIER_LABEL[p.tier] || "Blueprint"}
                      </div>
                    </div>
                    {p.active && <Check size={16} className="text-[#22C55E]" />}
                  </button>
                  {p.tier !== "elite" && (
                    <button data-testid={`profile-upgrade-${p.id}`} onClick={() => setUpgradeFor(upgradeFor === p.id ? null : p.id)}
                      className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#D4AF37] border border-[#D4AF37]/40 hover:bg-[#D4AF37]/10 rounded-full px-2 py-1 transition-colors flex-shrink-0">
                      <Sparkles size={11} /> Upgrade
                    </button>
                  )}
                </div>
                {upgradeFor === p.id && (
                  <div data-testid={`profile-upgrade-panel-${p.id}`} className="px-3 pb-3 pt-1 bg-white/[0.03]">
                    {membershipsOpen ? (
                      <>
                        <p className="text-[10px] text-white/50 mb-2">Unlock unlimited coach video feedback for {p.display_name || p.name}:</p>
                        <div className="flex gap-2">
                          {p.tier !== "academy" && (() => {
                            const elig = data?.sibling_eligible?.academy;
                            const price = elig ? data?.sibling_prices?.academy : data?.full_prices?.academy;
                            return (
                              <button data-testid={`profile-buy-academy-${p.id}`} onClick={() => buyForChild(p.id, "academy")} disabled={busy}
                                className="flex-1 flex flex-col items-center text-[11px] font-bold uppercase tracking-wider bg-[#1e3a5f] hover:bg-[#274b78] text-[#9fc5f8] py-2 rounded-md transition-colors disabled:opacity-60">
                                <span>Technical Track ${price}/mo</span>
                                {elig && <span className="text-[8px] text-[#9fc5f8]/70 flex items-center gap-0.5"><Users size={7} /> Sibling price</span>}
                              </button>
                            );
                          })()}
                          {(() => {
                            const elig = data?.sibling_eligible?.elite;
                            const price = elig ? data?.sibling_prices?.elite : data?.full_prices?.elite;
                            return (
                              <button data-testid={`profile-buy-elite-${p.id}`} onClick={() => buyForChild(p.id, "elite")} disabled={busy}
                                className="flex-1 flex flex-col items-center text-[11px] font-bold uppercase tracking-wider bg-[#D4AF37] hover:bg-[#c19f2e] text-black py-2 rounded-md transition-colors disabled:opacity-60">
                                <span>Elite ${price}/mo</span>
                                {elig && <span className="text-[8px] text-black/60 flex items-center gap-0.5"><Users size={7} /> Sibling price</span>}
                              </button>
                            );
                          })()}
                        </div>
                        {(data?.sibling_eligible?.academy || data?.sibling_eligible?.elite) && (
                          <p className="text-[9px] text-[#D4AF37] mt-1.5 text-center">🏷️ Sibling discount applied — your household already has a member.</p>
                        )}
                      </>
                    ) : (
                      <p data-testid={`profile-upgrade-soon-${p.id}`} className="text-[11px] text-white/50 bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-center">
                        Enrollment opens <b className="text-[#D4AF37]">October 1</b> — you'll be able to enroll {p.display_name || p.name} in the Technical Track or Elite right here.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {data?.can_add && (
            <div className="border-t border-white/8">
              {!adding ? (
                <button data-testid="profile-add-btn" onClick={() => setAdding(true)}
                  className="w-full flex items-center gap-2 px-3 py-3 text-sm font-bold text-[#C41E24] hover:bg-[#C41E24]/10 transition-colors">
                  <Plus size={16} /> Add a Player
                </button>
              ) : (
                <form onSubmit={doAdd} data-testid="profile-add-form" className="p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white/50">New Player</span>
                    <button type="button" onClick={() => setAdding(false)} className="text-white/40 hover:text-white"><X size={14} /></button>
                  </div>
                  <input data-testid="profile-add-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Player name" autoFocus
                    className="w-full bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
                  <input data-testid="profile-add-jersey" value={jersey} onChange={(e) => setJersey(e.target.value)} placeholder="Jersey # (optional)"
                    className="w-full bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]" />
                  <button data-testid="profile-add-submit" type="submit" disabled={busy}
                    className="w-full flex items-center justify-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider py-2.5 rounded-md transition-colors disabled:opacity-60">
                    {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Player
                  </button>
                  <p className="text-[10px] text-white/40 leading-snug">Shares your Blueprint access. Unlock unlimited video feedback by enrolling this player in the Technical Track.</p>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
